import ShopType from "@/enum/ShopType";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { reportOrderAllowedCallNo } from "@/services/api/order";
import { mobileRegExp } from "@/utils/RegExp";
import { useRequest } from "ahooks";
import { Button, Form, Input, message, Modal } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";

const LOCAL_STORAGE_KEY = "report_allowed_call_recent_numbers";
const MAX_RECENT_NUMBERS = 6;

interface ReportAllowedCallModalProps extends InjectBaseModalProps {
    shopId: number;
    orderId: number;
    virtualNo: string;
    shopType: ShopType;
}

const ReportAllowedCallModal: React.FC<ReportAllowedCallModalProps> = function (props) {
    const { open, onCancel, shopId, orderId, virtualNo, shopType } = props;

    const [form] = Form.useForm();
    const [recentNumbers] = useState<string[]>(() => {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) {
                const numbers = JSON.parse(stored);

                return numbers;
            }

            return [];
        } catch (error) {
            return [];
        }
    });

    // 保存号码到 localStorage
    const saveRecentNumber = (number: string) => {
        try {
            // 创建新列表，将新号码放在最前面
            const newNumbers = [number, ...recentNumbers.filter((n) => n !== number)];
            // 只保留最新的6条
            const trimmedNumbers = newNumbers.slice(0, MAX_RECENT_NUMBERS);

            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trimmedNumbers));
        } catch (error) {
            console.error("保存最近报备号码失败:", error);
        }
    };

    // 点击快速填充号码
    const handleQuickFill = (num) => {
        form.setFieldsValue({ callerNumber: num });
    };

    const { runAsync: handleOk, loading } = useRequest(
        async () => {
            const { callerNumber } = await form.validateFields();

            await reportOrderAllowedCallNo({ shopId, orderId, virtualNo, allowedCallNos: [callerNumber] });
            message.success("报备成功");

            saveRecentNumber(callerNumber);

            onCancel();
        },
        { manual: true },
    );

    return (
        <Modal
            title="报备主叫号码"
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            okText="确定报备"
            cancelText="取消"
            width={550}
            okButtonProps={{ style: { padding: "0 20px" }, loading }}
            cancelButtonProps={{ style: { padding: "0 20px" } }}
        >
            <div style={{ marginBottom: 24 }}>
                <div className="fb ml15" style={{ fontSize: 16 }}>
                    根据平台要求，拨打700号段虚拟号前必须先报备外呼主叫号码
                </div>
            </div>

            <Form form={form} layout="horizontal" labelCol={{ span: 4 }}>
                <Form.Item
                    label="主叫号码"
                    name="callerNumber"
                    rules={[
                        {
                            validator(_, value) {
                                if (!value) {
                                    return Promise.reject(new Error("请输入主叫号码"));
                                }

                                if (!mobileRegExp.test(value)) {
                                    return Promise.reject(new Error("手机号格式错误"));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder="请输入手机/座机号，报备后可给700虚拟号打电话" style={{ width: "93%" }} />
                </Form.Item>
            </Form>

            <div style={{ marginLeft: "16.6%", marginTop: -12 }}>
                {[ShopType.TAOBAO, ShopType.TAOBAO_DISTRIBUTION].includes(shopType) ? (
                    <div style={{ color: "#ff4d4f", marginBottom: 8, fontSize: 12 }}>
                        <div>淘宝平台可以店铺报备主叫号码，报备后可以拨打全店的700虚拟号</div>
                        <a
                            href="https://helpcenter.taobao.com/servicehall/knowledge_detail?kwdContentId=731401525709284353"
                            target="_blank"
                        >
                            查看官方公告
                        </a>
                    </div>
                ) : null}
            </div>

            <div style={{ marginLeft: "17px" }} className="text-gray text-small mt20">
                {recentNumbers.length ? (
                    <div>
                        <span>最近报备的号码，点击可直接添加：</span>
                        {recentNumbers.map((num) => (
                            <Button
                                key={num}
                                size="small"
                                onClick={() => handleQuickFill(num)}
                                style={{ borderRadius: 4, color: "#666", marginLeft: "3px" }}
                            >
                                {num}
                            </Button>
                        ))}
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default observer(ReportAllowedCallModal);
